'use client';

import { CHUNK_ERROR_PATTERNS } from '@/constants/safe-guard';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

let pendingNavUrl: string | null = null;

export function VersionGuard({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const pathname = usePathname();

    useEffect(() => {
        pendingNavUrl = null;
        sessionStorage.removeItem('vg-pending-url');
    }, [pathname]);

    useEffect(() => {
        function handleAnchorClick(e: MouseEvent): void {
            const anchor = (e.target as Element).closest('a[href]');
            if (!anchor) return;
            const href = anchor.getAttribute('href');
            if (href && href.startsWith('/')) {
                pendingNavUrl = href;
                sessionStorage.setItem('vg-pending-url', href);
            }
        }

        function triggerHardNav(source: string): void {
            const target = pendingNavUrl ?? window.location.href;
            pendingNavUrl = null;
            window.location.replace(target);
        }

        function handleError(event: ErrorEvent): void {
            if (isChunkError(event.error)) {
                event.preventDefault();
                triggerHardNav('isChunkError');
                return;
            }
            if (event.filename?.includes('/_next/static/')) {
                event.preventDefault();
                triggerHardNav('filename:_next/static');
            }
        }

        function handleUnhandledRejection(event: PromiseRejectionEvent): void {
            if (isChunkError(event.reason)) {
                event.preventDefault();
                triggerHardNav('unhandledrejection');
            }
        }

        document.addEventListener('click', handleAnchorClick, true);
        window.addEventListener('error', handleError, true);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            document.removeEventListener('click', handleAnchorClick, true);
            window.removeEventListener('error', handleError, true);
            window.removeEventListener(
                'unhandledrejection',
                handleUnhandledRejection,
            );
        };
    }, []);

    return <>{children}</>;
}

function isChunkError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const err = error as Error;
    if (err.name === 'ChunkLoadError') return true;
    return CHUNK_ERROR_PATTERNS.some((re) => re.test(err.message ?? ''));
}
